import { Injectable } from '@angular/core';
import { BullhornDataSourceApiService, BullhornEntityTypes } from 'datasource-service-lib';
import { IEntity, IUploadedFileInfo } from 'interfaces-lib';
import { lastValueFrom, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { IComposeMailService, IRecipient } from './compose-mail.service';
import { EntitySelection } from './EntitySelection';

type FileData = {
  entity: string;
  files: { id: number; fileName: string; clientCorporation?: boolean }[];
};

@Injectable({ providedIn: 'root' })
export class BullhornComposeMailService implements IComposeMailService {
  private _cacheKey: string | undefined;
  private static NOVO_TARGET_ORIGIN = 'https://app.bullhornstaffing.com';

  constructor(private apiService: BullhornDataSourceApiService) {}

  async launch(
    entity: IEntity,
    entitySelection: EntitySelection,
    uploadedFileInfo: IUploadedFileInfo[],
    subject: string,
    recipients?: IRecipient[],
    messageBody?: string
  ) {
    this.initialise();

    if (!recipients?.length) {
      recipients = await this.buildRecipients(entitySelection);
    }
    const jobOrderId = this.buildJobOrderReference(entitySelection);
    const candidates = this.buildCandidates(entitySelection);
    const fileData = this.buildFileData(entity, uploadedFileInfo); // it's not obvious if this should be entity or entitySelection.source
    const logAsCVSent = this.buildIsSendOut(entitySelection.source);

    console.log([
      entity,
      entitySelection,
      recipients,
      jobOrderId,
      candidates,
      fileData,
      logAsCVSent,
      messageBody
    ]);

    this.open({
      subject: subject || '',
      toRecipients: recipients,
      jobOrderId: jobOrderId,
      candidates: candidates,
      fileData: fileData,
      logAsCVSent: logAsCVSent,
      message: messageBody,
    });
  }

  private initialise(): void {
    if (this._cacheKey) { return; }

    this._cacheKey = Date.now().toString();
    const registerMessage = {
      action: 'register',
      message: {},
      cacheKey: this._cacheKey,
      uuid: window.name,
    };
    window.parent.postMessage(
      registerMessage,
      BullhornComposeMailService.NOVO_TARGET_ORIGIN
    );

    // force the bowling alley title of the ADv2 window to be correct
    const windowTitle = window.name.substring(0, window.name.length - 3); // take off "Wnd"
    const updateTitleMessage = {
      action: 'update-bowling-alley-title',
      title: windowTitle,
      uuid: window.name,
    };
    window.parent.postMessage(
      updateTitleMessage,
      BullhornComposeMailService.NOVO_TARGET_ORIGIN
    );
  }

  private open(data: any): void {
    const message: any = {
      action: 'open',
      cacheKey: this._cacheKey,
      uuid: window.name,
      message: {
        type: 'Content',
        app: 'actions',
        action: 'compose-message',
        title: 'Compose Message',
        data: {
          messageType: 'normal',
          isSendout: data.logAsCVSent,
          subject: data.subject,
          to: data.toRecipients,
          jobOrder: data.jobOrderId,
          candidates: data.candidates,
          fileData: data.fileData,
          message: data.message,
        },
      },
    };

    window.parent.postMessage(
      message,
      BullhornComposeMailService.NOVO_TARGET_ORIGIN
    );
  }

  private buildRecipients(
    entitySelection: EntitySelection
  ): Promise<{ id: number }[] | undefined> {
    const entity = entitySelection.source;

    if (
      entity.type === BullhornEntityTypes.JobOrder ||
      entity.type === BullhornEntityTypes.JobSubmission
    ) {
      let jobOrder: IEntity | undefined;
      if (entity.type === BullhornEntityTypes.JobOrder) {
        jobOrder = entity;
      } else if (entity.type === BullhornEntityTypes.JobSubmission) {
        // the destinations should have the JobOrder in them (if not could check the 'primary' entity)
        jobOrder = entitySelection.destinations.find(
          (e) => e.type === BullhornEntityTypes.JobOrder
        );
        if (!jobOrder) {
          throw new Error(
            'Could not find JobOrder destination for JobSubmission.'
          );
        }
      }

      if (jobOrder) {
        return lastValueFrom(this.apiService
          .get(jobOrder, { fields: 'clientContact(id)' })
          .pipe(map((response) => [{ id: response.clientContact.id }]))
          );
      }
    }
    return lastValueFrom(of([]));
  }

  private buildJobOrderReference(
    entitySelection: EntitySelection
  ): { id: number } | '' {
    const entity = entitySelection.source;

    if (entity.type === BullhornEntityTypes.JobOrder) {
      return { id: this.toNumber(entity.id) };
    }

    if (entity.type === BullhornEntityTypes.JobSubmission) {
      // the destinations should have the JobOrder in them (if not could check the 'primary' entity)
      const jobOrder = entitySelection.destinations.find(
        (e) => e.type === BullhornEntityTypes.JobOrder
      );
      if (!jobOrder) {
        throw new Error(
          'Could not find JobOrder destination for JobSubmission.'
        );
      }
      return { id: this.toNumber(jobOrder.id) };
    }

    // magic
    return '' as '';
  }

  private buildCandidates(entitySelection: EntitySelection): { id: number }[] {
    const entity = entitySelection.source;

    if (entity.type === BullhornEntityTypes.Candidate) {
      return [{ id: this.toNumber(entity.id) }];
    }

    if (entity.type === BullhornEntityTypes.JobSubmission) {
      // the destinations should have the Candidate
      const candidate = entitySelection.destinations.find(
        (e) => e.type === BullhornEntityTypes.Candidate
      );
      if (!candidate) {
        throw new Error(
          'Could not find Candidate destination for JobSubmission.'
        );
      }
      return [{ id: this.toNumber(candidate.id) }];
    }

    return [];
  }

  private buildFileData(
    entity: IEntity,
    uploadedFileInfo: IUploadedFileInfo[]
  ): FileData {
    const isClientCorporation =
      entity.type === BullhornEntityTypes.ClientCorporation;

    const fileData: FileData = {
      entity: isClientCorporation ? 'company' : 'non-company',
      files: [],
    };

    if (uploadedFileInfo.length === 0) { return fileData; }

    const fileEntityType = uploadedFileInfo[0].path[0]; // path = <entity-type>,<entity-id>,<file-id>
    const isFileFromClientCorporation =
      fileEntityType === BullhornEntityTypes.ClientCorporation;

    fileData.files = uploadedFileInfo
      .filter((fileInfo) => fileInfo.path[0] === fileEntityType)
      .map((fileInfo) => {
        const attachment: {
          id: number;
          fileName: string;
          clientCorporation?: boolean;
        } = {
          id: this.toNumber(fileInfo.path.slice(-1)[0]),
          fileName: fileInfo.fileName,
        };
        if (isFileFromClientCorporation) { attachment.clientCorporation = true; }
        return attachment;
      });

    return fileData;
  }

  private buildIsSendOut(entity: IEntity): boolean {
    return (
      entity.type === BullhornEntityTypes.Candidate ||
      entity.type === BullhornEntityTypes.JobSubmission
    );
  }

  private toNumber(id: string): number {
    return parseInt(id, 10);
  }
}
